/**
 * Skeleton component for the Indigo design system.
 *
 * A neutral placeholder that reserves the shape of content while it loads.
 * Per the design guide, skeletons are the loading variant for *text content*;
 * use `LoadingPlaceholder` for whole sections/dashboards and `LoadingSpinner`
 * for in-progress tasks.
 *
 * Features:
 * - Text and block variants
 * - Standard sizes with per-instance width/height overrides
 * - Multi-line text placeholders (last line is shortened)
 * - Design-token colors, so it follows every theme
 * - Respects `prefers-reduced-motion`
 * - Announced as "Loading…" to screen readers
 *
 * @example
 * ```tsx
 * // Single text line filling its container
 * <Skeleton />
 *
 * // A short value placeholder, e.g. standing in for a resolved entity name
 * <Skeleton width="60%" />
 *
 * // Paragraph placeholder
 * <Skeleton lines={3} />
 *
 * // Block placeholder for a card/avatar area
 * <Skeleton variant="block" width="120px" height="72px" />
 * ```
 */
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import "./Skeleton.css";

const skeletonVariants = cva("indigo-skeleton", {
  variants: {
    variant: {
      text: "indigo-skeleton--text",
      block: "indigo-skeleton--block",
    },
    size: {
      sm: "indigo-skeleton--sm",
      md: "indigo-skeleton--md",
      lg: "indigo-skeleton--lg",
    },
  },
  defaultVariants: {
    variant: "text",
    size: "md",
  },
});

export interface SkeletonProps extends VariantProps<typeof skeletonVariants> {
  /**
   * Shape of the placeholder.
   * - "text": a rounded line sized to a text row
   * - "block": a larger rounded rectangle for cards, thumbnails, avatars
   * @default "text"
   */
  variant?: "text" | "block";

  /**
   * Standard height. `text` maps to the xs/sm/md line heights (12/16/20px),
   * `block` to 24/32/48px.
   * @default "md"
   */
  size?: "sm" | "md" | "lg";

  /**
   * CSS width of the placeholder.
   * @default "100%"
   */
  width?: string;

  /**
   * CSS height. Overrides the height set by `size`.
   */
  height?: string;

  /**
   * Number of stacked lines. Only meaningful for `variant="text"`; the last
   * line is rendered shorter so the block reads as prose rather than a bar.
   * @default 1
   */
  lines?: number;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Label announced to screen readers.
   * @default "Loading…"
   */
  ariaLabel?: string;

  "data-testid"?: string;
}

/** Width of the final line of a multi-line text skeleton. */
const LAST_LINE_WIDTH = "60%";

/**
 * Skeleton component
 *
 * @param props - Component props (@see SkeletonProps)
 * @returns A React component for displaying a content placeholder
 */
export const Skeleton = ({
  variant = "text",
  size = "md",
  width = "100%",
  height,
  lines = 1,
  className,
  ariaLabel = "Loading…",
  "data-testid": dataTestId,
}: SkeletonProps) => {
  const lineCount = variant === "text" ? Math.max(1, lines) : 1;

  return (
    <div
      className={clsx("indigo-skeleton-container", className)}
      style={{ width }}
      role="status"
      data-testid={dataTestId}
    >
      {Array.from({ length: lineCount }, (_, index) => (
        <div
          // Lines are positional and never reordered, so the index is a stable key.
          key={index}
          className={skeletonVariants({ variant, size })}
          style={{
            height,
            width:
              lineCount > 1 && index === lineCount - 1
                ? LAST_LINE_WIDTH
                : undefined,
          }}
        />
      ))}
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );
};

export { skeletonVariants };
export default Skeleton;
